import L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import 'leaflet.fullscreen';
import 'leaflet.markercluster';
import { labels, lang } from './map-labels';
import { detailTemplate } from './map-detail';
import { createFocusTrap } from 'focus-trap';
import { Tooltip } from '@3pc/tooltip';
import { $ } from '../../../shared/js/utils/dom';

export class MapMapbox {
  constructor(params) {
    this.container = params.container;
    this.mapContainer =
      params.mapContainer || this.container.querySelector('.map__mapbox');
    this.mapData =
      params.mapData || JSON.parse(this.mapContainer.dataset.mapData);
    this.mapId = this.mapContainer.getAttribute('id');
    this.mapDetail =
      params.mapDetail || this.container.querySelector('.map__detail');
    this.mapDetailContent =
      params.mapContent || this.mapDetail.querySelector('.map__detail-content');
    this.mapFullscreenContainer = document.querySelector('.map__map');
    this.mapDetailClose =
      params.mapDetailClose ||
      this.mapDetail.querySelector('.map__detail-close');
    this.mapDetailFocusTrapInstance = undefined;
    this.mapTooltipInstances = [];
    this.mapTooltipMarkerInstances = [];
    this.defaultCoordinates = params.defaultCoordinates || [51.5167, 9.9167];
    this.defaultZoom = params.defaultZoom || 6;
    this.detailZoom = params.detailZoom || 10;
    this.markers = [];
    this.markersLayer = undefined;
    this.markerGroup = null;
    this.zoomLevel = params.zoomLevel || 5;
    this.activeMarker = null;
    this.pinIcon = (srOnly = false, isActive = false) => {
      return `
        <span class="icon icon--map-pin ${
          isActive ? 'icon--map-pin-active' : ''
        }">
          <svg class="icon__svg" role="img" aria-hidden="true">
            <use href="#icon-map-pin"></use>
          </svg>
          ${srOnly ? '<span class="sr-only">' + srOnly + '</span>' : ''}
        </span>
  `;
    };
    this.isInitial = true;
  }

  initMap = () => {
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

    // init map
    this.osm = L.map(this.mapId, {
      dragging: !L.Browser.mobile,
      zoomControl: false, // we're using custom zoom controls instead
      fullscreenControl: true,
      gestureHandling: true,
      gestureHandlingOptions: {
        duration: 2000,
        text: {
          touch: labels.gestureTouch[lang],
          scroll: labels.gestureScroll[lang],
          scrollMac: labels.gestureScrollMac[lang],
        },
      },
      attributionControl: false,
      minZoom: 3,
      maxZoom: 17,
      zoomLevel: 6,
    })
      .on('zoom', this.resetMarkerTooltips.bind(this))
      .on('unload', this.unloadHandler.bind(this));

    // attribution
    L.control
      .attribution({
        prefix:
          '<a class="styled-link styled-link--external rte-icon-fix" href="https://leafletjs.com" target="_blank" rel="noopener noreferrer"><span class="sr-only">Externer Link:</span>Leaflet<span class="sr-only">A JavaScript library for interactive maps</span></a>',
      })
      .addTo(this.osm);

    const fullscreen = L.control.fullscreen({
      fullscreenElement: this.mapFullscreenContainer,
    });

    fullscreen.addTo(this.osm);

    const apiKey =
      'pk.eyJ1IjoiZHVrcHJlc3NlIiwiYSI6ImNsdHhhNWFmNDAzNnQycXBrOWd6dTJncW0ifQ.gJqSQj_OPTLHYsvebMcucA';
    const styleKey = 'dukpresse/clwuu5rk1013e01qscbr5hs0e';

    // add tiles
    const tiles = new L.TileLayer(
      `https://api.mapbox.com/styles/v1/${styleKey}/tiles/{z}/{x}/{y}?access_token=${apiKey}`,
      {
        attribution: '© <a href="https://www.mapbox.com/">Mapbox</a>',
        accessToken: apiKey,
      }
    );
    tiles.addTo(this.osm);

    this.initMarkers();
    this.setMapView();
    this.bindControls();
    this.initTooltips();
    this.initMarkerTooltips();
  };

  initMarkers = () => {
    const markers = L.markerClusterGroup({
      disableClusteringAtZoom: 18,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: false,
      showCoverageOnHover: false,
      spiderLegPolylineOptions: {
        weight: 0,
        opacity: 0,
      },
      //  spiderfyOnEveryZoom: true,
      iconCreateFunction: cluster => {
        return L.divIcon({
          html: `
          <span class="map__cluster-marker-amount" aria-hidden="true">
            ${cluster.getChildCount()}
          </span>
          <span class="map__cluster-marker-icon icon--osm-map-pin-cluster">
            <svg class="icon__svg" role="img" aria-hidden="true">
              <use href="#icon-map-pin-cluster"></use>
            </svg>
            <span class="sr-only">${cluster.getChildCount()} Standorte auffächern</span>
          </span>`,
          className: 'map__cluster-marker',
          iconSize: new L.Point(66, 66),
          iconAnchor: [33, 66],
        });
      },
    })
      .on('clusterclick', e => {
        e.layer.zoomToBounds({ padding: [20, 20] });
        this.mapContainer.focus();
      })
      .on('clusterkeypress', () => {
        this.mapContainer.focus();
      })
      .on('clustermouseover', e => {
        this.onMarkerHasCursor(e.layer);
      })
      .on('clustermouseout', e => {
        this.onMarkerLostCursor(e.layer);
      })
      .on('spiderfied', e => {
        this.resetMarkerTooltips();
        const clusterMarker = e.cluster._icon;
        if (parseFloat(clusterMarker.style.opacity) < 1)
          clusterMarker.style.display = 'none';
      })
      .on('unspiderfied', e => {
        this.resetMarkerTooltips();
        const clusterMarker = e.cluster._icon;
        if (parseFloat(clusterMarker.style.opacity) >= 1)
          clusterMarker.style.display = 'flex';
      });

    this.markers = [];

    this.mapData.length &&
      this.mapData.forEach(result => {
        if (result.geometry.type === 'Point') {
          let marker = null;

          // set marker icon
          const icon = L.divIcon({
            html: this.pinIcon(result.title),
            iconSize: [40, 49],
            iconAnchor: [20, 49],
            className: `map__marker`,
          });

          // convert coordinate string to floats
          const lon = parseFloat(result.geometry.coordinates[0]);
          const lat = parseFloat(result.geometry.coordinates[1]);

          // add markers
          marker = L.marker([lat, lon], {
            icon,
            zIndexOffset: result.priority * 30, // adjust marker stacking order according to the location's priority (see search-readme)
            riseOnHover: true,
          })
            .on('click', e => {
              this.onMarkerClick(e.target);
            })
            .on('keydown', e => {
              if (e.originalEvent.key === 'Enter') {
                this.onMarkerClick(e.target);
              }
            })
            .on('mouseover', e => {
              this.onMarkerHasCursor(e.target);
            })
            .on('mouseout', e => {
              this.onMarkerLostCursor(e.target);
            });
          Object.assign(marker, result);
          this.markers.push(marker);

          markers.addLayer(marker);
        } else {
          L.geoJSON(result.geometry, {
            style: function (feature) {
              switch (feature.geometry.type) {
                case 'MultiLineString':
                  return { color: '#e4ff3e' };
                case 'MultiPolygon':
                  return {
                    color: '#e4ff3e',
                    fill: true,
                    fillColor: '#e4ff3e',
                    fillOpacity: 0.6,
                  };
                case 'Polygon':
                  return {
                    color: '#e4ff3e',
                    fill: true,
                    fillColor: '#e4ff3e',
                    fillOpacity: 0.6,
                  };
                default:
                  return {
                    color: '#e4ff3e',
                  };
              }
            },
            onEachFeature: (feature, layer) => {
              layer.on('click', e => {
                this.onMarkerClick(e.target);
                this.osm.fitBounds(layer.getBounds(), { padding: [450, 100] });
              });
              layer.on('keydown', e => {
                if (e.originalEvent.key === 'Enter') {
                  this.onMarkerClick(e.target);
                }
              });
              Object.assign(layer, result);
              this.markers.push(layer);
              markers.addLayer(layer);
            },
          }).addTo(this.osm);
        }
      });

    this.osm.addLayer(markers);
    this.markersLayer = markers;
  };

  destroyMarkers = () => {
    this.destroyMarkerTooltips();
    this.osm.removeLayer(this.markersLayer);
  };

  updateMarkers = (markers, isReset = false) => {
    this.destroyMarkers();
    this.mapData = typeof markers === 'string' ? JSON.parse(markers) : markers;
    this.initMarkers();
    // Position map
    if (isReset) {
      this.resetMapView();
    } else {
      this.updateMapPosition();
    }
  };

  updateMapPosition = () => {
    // Move map to frame first n markers
    this.fitBounds(this.markers);
  };

  setMapView = () => {
    if (this.isInitial) {
      this.osm.setView(this.defaultCoordinates, this.defaultZoom);
    }
    // If no data is available set a default view in order to trigger map tile rendering (without none are loaded)
    if (this.mapData.length === 0)
      this.osm.setView(new L.LatLng(51.5167, 9.9167), 5); // Center on Germany's middle

    this.isInitial = false;
  };

  resetMapView = () => {
    this.osm.setView(this.defaultCoordinates, this.defaultZoom); // Center on Germany's middle
  };

  fitBounds = markersSelection => {
    const markers = markersSelection || this.markers;
    if (markers.length) this.featureGroup = new L.FeatureGroup(markers);
    this.featureGroup &&
      this.osm.fitBounds(this.featureGroup.getBounds(), {
        padding: [20, 20],
      });
  };

  bindControls = () => {
    this.container
      .querySelector('.map__zoom-in')
      .addEventListener('click', this.zoomIn);
    this.container
      .querySelector('.map__zoom-out')
      .addEventListener('click', this.zoomOut);
    this.container
      .querySelector('.map__fullscreen')
      .addEventListener('click', this.toggleFullscreen);
  };

  unbindControls = () => {
    this.container
      .querySelector('.map__zoom-in')
      .removeEventListener('click', this.zoomIn);
    this.container
      .querySelector('.map__zoom-out')
      .removeEventListener('click', this.zoomOut);
    this.container
      .querySelector('.map__fullscreen')
      .removeEventListener('click', this.toggleFullscreen);
  };

  zoomIn = () => {
    this.osm.setZoom(this.osm.getZoom() + 1);
  };

  zoomOut = () => {
    this.osm.setZoom(this.osm.getZoom() - 1);
  };

  toggleFullscreen = () => {
    this.osm.fullscreenControl.toggleFullScreen();
  };

  onMarkerClick = async marker => {
    await this.resetMarkersAndMap();
    this.activeMarker = marker;
    this.setActiveMarkerIcon();
    this.updateDetail();
    this.openDetail();
  };

  onMarkerHasCursor = marker => {
    if (marker === this.activeMarker) return;

    const icon = marker._icon;
    icon.style.transform += ' scale(1.1)';
  };

  onMarkerLostCursor = marker => {
    const icon = marker._icon;
    icon.style.transform = icon.style.transform.replace(' scale(1.1)', '');
  };

  resetMarkersAndMap = () => {
    this.activeMarker = null;
    this.markers.forEach(marker => {
      const markerIcon = marker.options.icon;
      if (!markerIcon) return;
      markerIcon.options.iconSize = [40, 49];
      markerIcon.options.iconAnchor = [20, 49];
      markerIcon.options.className = `map__marker`;
      markerIcon.options.html = this.pinIcon(marker.title);
      marker.setIcon(markerIcon);
      marker.setZIndexOffset(marker.priority * 30);
    });
  };

  setActiveMarkerIcon = () => {
    const markerIcon = this.activeMarker.options.icon;
    if (!markerIcon) return;
    markerIcon.options.iconSize = [48, 59];
    markerIcon.options.iconAnchor = [24, 59];
    markerIcon.options.className = `map__marker map__marker--active`;
    markerIcon.options.html = this.pinIcon(this.activeMarker.title, true);
    this.activeMarker.setIcon(markerIcon);

    this.osm.setView(this.activeMarker.getLatLng());
  };

  initDetail = () => {
    this.mapDetailClose.addEventListener('click', this.closeDetail.bind(this));
    this.mapContainer.addEventListener('click', () => {
      if (!this.activeMarker) {
        this.closeDetail();
      }
      this.activeMarker = false;
    });
    this.closeDetail();
  };

  updateDetail = () => {
    this.mapDetailContent.innerHTML = detailTemplate(this.activeMarker);

    const copyrightButton = $('.caption__button-copyright');

    if (!copyrightButton) return;
    const container = copyrightButton.closest('.caption');

    copyrightButton.addEventListener('click', event => {
      event.stopPropagation();
      const buttonTarget = event.currentTarget;
      const isExpanded = buttonTarget.getAttribute('aria-expanded') === 'true';

      buttonTarget.setAttribute('aria-expanded', !isExpanded);
      container.classList.toggle('is-open');
    });
  };

  closeDetail = e => {
    e?.preventDefault();
    e?.stopPropagation();
    e?.stopImmediatePropagation();
    this.removeFocusTrap();
    this.mapDetail.classList.add('is-hidden');
    document.body.classList.remove('map-detail-open');
    this.activeMarker?._icon?.focus();
    this.resetMarkersAndMap();
  };

  openDetail = e => {
    e?.preventDefault();
    e?.stopPropagation();
    e?.stopImmediatePropagation();
    document.body.classList.add('map-detail-open');
    this.mapDetail.classList.remove('is-hidden');
    this.addFocusTrap();
  };

  addFocusTrap() {
    this.mapDetailFocusTrapInstance = createFocusTrap(this.mapDetail, {
      clickOutsideDeactivates: true,
      initialFocus: this.mapDetailClose,
    });
    this.mapDetailFocusTrapInstance.activate();
  }

  removeFocusTrap() {
    this.mapDetailFocusTrapInstance?.deactivate();
  }

  initTooltips() {
    const element = document.querySelector('.leaflet-control-zoom-fullscreen');
    const mapFullscreenTooltip = new Tooltip(element, {
      contentAttribute: 'title',
    });
    mapFullscreenTooltip.init();
    this.mapTooltipInstances.push(mapFullscreenTooltip);
  }

  initMarkerTooltips() {
    const markers = document.querySelectorAll(
      '.map__marker, .map__cluster-marker'
    );
    markers.forEach(element => {
      const tooltip = new Tooltip(element, {
        contentSelector: '.sr-only',
      });

      tooltip.init();
      this.mapTooltipMarkerInstances.push(tooltip);
    });
  }

  destroyMarkerTooltips() {
    this.mapTooltipMarkerInstances.forEach(instance => {
      instance.destroy();
    });

    this.mapTooltipMarkerInstances = [];
  }

  resetMarkerTooltips = async e => {
    await this.destroyMarkerTooltips();
    this.initMarkerTooltips();
  };

  updateMapData = () => {
    this.mapData = JSON.parse(this.mapContainer.dataset.mapData);
  };

  destroy = () => {
    if (this.osm && this.osm.remove) {
      this.osm.remove();
    }
  };

  unloadHandler = () => {
    this.osm.off();
    this.osm.scrollWheelZoom.disable();
    this.osm.dragging.disable();
    this.resetMarkersAndMap();
    this.unbindControls();
    this.mapTooltipInstances.forEach(tooltip => tooltip.destroy());
    this.destroyMarkerTooltips();
    this.closeDetail();
    this.mapDetailClose.removeEventListener('click', this.closeDetail);
  };

  init = () => {
    this.initMap();
    this.initDetail();
  };
}
